// Hook owning the pre-send attachment list and the all-or-nothing S3 upload.

import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { logger } from "@amzn/afxtaco-utilities";
import { invalidateUploadedFiles } from "../../../redux/reducers/Projects";
import { getAssetUploadUrl, uploadFileToS3 } from "../../api/assets/assets";
import { MAX_UPLOAD_FILES } from "@amzn/afxtaco-types";
import { SentAttachment, UploadFile, UploadFileStatus } from "./types";
import { validateFile } from "./fileUploadUtils";

// User-facing message for chips that exceed the per-message count limit.
const COUNT_LIMIT_REASON = `You can attach at most ${MAX_UPLOAD_FILES} files per message`;

// Maps a valid browser File to a READY UploadFile carrying the bytes.
function toReadyUploadFile(file: File): UploadFile {
  return {
    id: uuidv4(),
    name: file.name,
    size: file.size,
    type: file.type,
    status: UploadFileStatus.READY,
    file,
  };
}

// Maps an invalid browser File to an ERROR UploadFile. `isCountError` flags a
// (recomputable) over-limit error rather than a sticky per-file validation error.
function toErrorUploadFile(
  file: File,
  reason: string,
  isCountError = false,
): UploadFile {
  return {
    id: uuidv4(),
    name: file.name,
    size: file.size,
    type: file.type,
    status: UploadFileStatus.ERROR,
    errorMessage: reason,
    isCountError,
    file,
  };
}

/**
 * Re-derives the per-message count-limit error across the list.
 *
 * The over-limit condition is a property of a file's *position* in the list,
 * not of the file itself, so it must be recomputed whenever the list changes
 * (add or remove). This walks the list in order, counting only files that are
 * NOT failing per-file validation, and:
 *   - marks every counted file beyond {@link MAX_UPLOAD_FILES} as a count error, and
 *   - demotes any now-within-limit count-error chip back to READY.
 *
 * Sticky per-file errors (bad type/size/empty — `isCountError` falsy) are left
 * untouched: they neither consume a slot nor get cleared by removing others.
 */
function reconcileCountErrors(files: UploadFile[]): UploadFile[] {
  let withinLimitCount = 0;
  return files.map((file) => {
    // Per-file validation errors are sticky and don't occupy a count slot.
    if (file.status === UploadFileStatus.ERROR && !file.isCountError) {
      return file;
    }

    withinLimitCount += 1;
    const overLimit = withinLimitCount > MAX_UPLOAD_FILES;

    if (overLimit) {
      // Mark (or keep) this chip as a count error.
      if (file.isCountError) return file;
      return {
        ...file,
        status: UploadFileStatus.ERROR,
        errorMessage: COUNT_LIMIT_REASON,
        isCountError: true,
      };
    }

    // Within the limit: clear any stale count error back to READY.
    if (file.isCountError) {
      return {
        ...file,
        status: UploadFileStatus.READY,
        errorMessage: undefined,
        isCountError: false,
      };
    }
    return file;
  });
}

export interface UseFileUploadResult {
  // Current pre-send attachments.
  files: UploadFile[];
  // Whether an upload batch is currently in flight.
  isUploading: boolean;
  // Validates and appends newly-selected files (invalid ones become ERROR chips).
  addFiles: (selected: File[]) => void;
  // Removes a single attachment by id.
  removeFile: (id: string) => void;
  // Clears all attachments (e.g. after a successful send).
  clearFiles: () => void;
  // Uploads all files to S3, returning the uploaded attachments (with their
  // final s3Keys) on success, or null if any upload failed (the failed chip is
  // marked ERROR). Returning the list directly avoids reading back the `files`
  // state, which is stale inside the same event handler that triggered the
  // upload (React state updates are not visible until the next render).
  uploadAll: (projectId: string) => Promise<SentAttachment[] | null>;
}

export function useFileUpload(): UseFileUploadResult {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const dispatch = useDispatch();

  const addFiles = useCallback((selected: File[]) => {
    if (selected.length === 0) return;

    setFiles((prev) => {
      // Validate each new file on its own merits (type/size/empty). Genuinely
      // invalid files become sticky ERROR chips; valid ones start READY.
      const mapped = selected.map((file) => {
        const result = validateFile(file);
        return result.valid
          ? toReadyUploadFile(file)
          : toErrorUploadFile(file, result.reason);
      });
      // Re-derive the per-message count limit across the combined list so only
      // the files beyond MAX_UPLOAD_FILES are flagged (recomputed on every add).
      return reconcileCountErrors([...prev, ...mapped]);
    });
  }, []);

  const removeFile = useCallback((id: string) => {
    // Re-derive count errors after removal so dropping the extras clears any
    // stale red chips and restores the survivors to READY.
    setFiles((prev) =>
      reconcileCountErrors(prev.filter((file) => file.id !== id)),
    );
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
  }, []);

  // Marks a single file as ERROR with the given message.
  const markError = useCallback((id: string, reason: string) => {
    setFiles((prev) =>
      prev.map((file) =>
        file.id === id
          ? { ...file, status: UploadFileStatus.ERROR, errorMessage: reason }
          : file,
      ),
    );
  }, []);

  // Marks a single file as UPLOADING, clearing any prior error.
  const markUploading = useCallback((id: string) => {
    setFiles((prev) =>
      prev.map((file) =>
        file.id === id
          ? {
              ...file,
              status: UploadFileStatus.UPLOADING,
              errorMessage: undefined,
            }
          : file,
      ),
    );
  }, []);

  // Marks a single file as READY and records its final S3 key.
  const markReady = useCallback((id: string, s3Key: string) => {
    setFiles((prev) =>
      prev.map((file) =>
        file.id === id
          ? { ...file, status: UploadFileStatus.READY, s3Key }
          : file,
      ),
    );
  }, []);

  const uploadAll = useCallback(
    async (projectId: string): Promise<SentAttachment[] | null> => {
      if (files.length === 0) return [];

      setIsUploading(true);
      try {
        // Accumulate uploaded attachments so we can return them directly to the
        // caller (the `files` state won't reflect markReady() until re-render).
        const uploaded: SentAttachment[] = [];
        // Upload sequentially so a failure aborts the batch promptly.
        for (const file of files) {
          if (!file.file) {
            // Defensive: a chip without underlying bytes can't be uploaded.
            markError(file.id, "File is no longer available");
            return null;
          }

          markUploading(file.id);
          try {
            const { data } = await getAssetUploadUrl({
              projectId,
              filename: file.name,
              contentType: file.type || "application/octet-stream",
              fileSize: file.size,
            });
            await uploadFileToS3(
              { url: data.url, fields: data.fields },
              file.file,
            );
            markReady(file.id, data.s3Key);
            uploaded.push({
              name: file.name,
              size: file.size,
              type: file.type,
              s3Key: data.s3Key,
            });
          } catch (error) {
            logger.error({
              loggerMsg: "[useFileUpload] File upload failed",
              filename: file.name,
              error,
            });
            markError(
              file.id,
              error instanceof Error ? error.message : "Upload failed",
            );
            // Anything already in S3 is there for good, so refresh the Overview
            // list for it even though the batch is being abandoned. Otherwise a
            // partial failure leaves those files invisible until something
            // unrelated triggers a refetch.
            if (uploaded.length > 0) {
              dispatch(invalidateUploadedFiles(projectId));
            }
            // Abort the whole batch on the first failure.
            return null;
          }
        }
        // Tell the Overview's upload list to re-fetch. Without this it only
        // refreshed when something else invalidated assets or the component
        // remounted — which is why a new file appeared only after switching
        // tabs and coming back.
        if (uploaded.length > 0) {
          dispatch(invalidateUploadedFiles(projectId));
        }
        return uploaded;
      } finally {
        setIsUploading(false);
      }
    },
    [files, markError, markReady, markUploading, dispatch],
  );

  return {
    files,
    isUploading,
    addFiles,
    removeFile,
    clearFiles,
    uploadAll,
  };
}
